import React, { useEffect } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Grid, MenuItem } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startOfDay } from 'date-fns';

import { FormTextField, useDebouncedEffect, FormNumberField, FormSelect } from '@lib/common';
import { StayInfoFilters, BookedByUserType } from '@lib/state';
import { CorporateAccountModel } from '@lib/state/api/generated/reservations';

interface StayInfoFilterProps {
  filters: StayInfoFilters;
  onChange: (filters: StayInfoFilters) => void;
  userId?: string;
  corporateAccounts?: Array<CorporateAccountModel>;
}

type StayInfoFilterForm = Omit<StayInfoFilters, 'recordNumber'> & {
  recordNumber: string;
};

export const StayInfoFilter: React.FC<StayInfoFilterProps> = ({
  filters,
  onChange,
  userId,
  corporateAccounts,
}) => {
  const form = useForm<StayInfoFilterForm>({
    defaultValues: {
      ...filters,
      recordNumber: filters.recordNumber ? String(filters.recordNumber) : '',
    },
  });

  useEffect(() => {
    form.register('start');
    form.register('end');
    form.register('bookedBy');
    form.register('corporateAccountId');

    // eslint-disable-next-line
  }, []);

  const {
    start,
    end,
    recordNumber,
    firstName,
    lastName,
    crew,
    bookedBy,
    corporateAccountId,
  } = form.watch({
    nest: true,
  });

  useDebouncedEffect(
    () => {
      onChange({
        start,
        end,
        recordNumber: recordNumber ? Number(recordNumber) : undefined,
        firstName,
        lastName,
        crew,
        bookedBy,
        userId,
        corporateAccountId,
      });
    },
    1000,
    [start, end, recordNumber, firstName, lastName, bookedBy, crew, corporateAccountId]
  );

  return (
    <FormContext {...form}>
      <Grid container spacing={1}>
        <Grid item lg={4} sm={4} xs={12}>
          <DatePicker
            name="start"
            label="Check-In After"
            format="MMMM d"
            value={start ?? null}
            error={!!form.errors.start}
            onChange={value => form.setValue('start', (value && startOfDay(value)) ?? undefined)}
            color="primary"
            InputProps={{ endAdornment: <FontAwesomeIcon icon="calendar-week" /> }}
            size="small"
            clearable
            fullWidth
            autoOk
          />
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          <DatePicker
            name="end"
            label="Check-In Before"
            format="MMMM d"
            value={end ?? null}
            error={!!form.errors.end}
            onChange={value => form.setValue('end', (value && startOfDay(value)) ?? undefined)}
            color="primary"
            InputProps={{ endAdornment: <FontAwesomeIcon icon="calendar-week" /> }}
            size="small"
            clearable
            fullWidth
            autoOk
          />
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          <FormSelect defaultValue={''} name="bookedBy" label="Booked By" fullWidth>
            <MenuItem value={''}>Any</MenuItem>
            <MenuItem value={BookedByUserType.You}>Booked By You</MenuItem>
            <MenuItem value={BookedByUserType.Delegate}>Booked By Delegate</MenuItem>
            <MenuItem value={BookedByUserType.Staff}>Booked By Hotel Staff</MenuItem>
          </FormSelect>
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          <FormNumberField name="recordNumber" label="Reservation Number" fullWidth size="small" />
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          <FormTextField name="firstName" label="First Name" fullWidth size="small" />
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          <FormTextField name="lastName" label="Last Name" fullWidth size="small" />
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          <FormTextField name="crew" label="Crew" fullWidth size="small" />
        </Grid>
        <Grid item lg={4} sm={4} xs={12}>
          {corporateAccounts && corporateAccounts?.length > 1 && (
            <FormSelect
              defaultValue={''}
              name="corporateAccountId"
              label="Corporate Accout"
              fullWidth
            >
              <MenuItem value={''}>Any</MenuItem>
              {corporateAccounts.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </FormSelect>
          )}
        </Grid>
      </Grid>
    </FormContext>
  );
};
